/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState, useEffect, useCallback } from "react";
import { Dimensions, TextInput, View } from "react-native";
import Icon from "react-icons-kit";
import { exclamation } from "react-icons-kit/metrize/exclamation";

import { useSelector } from "react-redux";
import { RootState } from "store/types";

import { Button, Text, styles } from "UpduoLibrary";
import {
  skalePrimaryBlue,
  skaleTextGray,
  skaleCommunityGray,
  skaleBlack,
  colorWithOpacity,
} from "config/UpduoColors";

const screenHeight = Dimensions.get("screen").height;
const textColor = skaleTextGray;

interface InternalIdViewProps {
  isLoading: boolean;
  onSubmitInternalId: Function;
  hasError: boolean;
}

export default function InternalIdView({
  isLoading,
  onSubmitInternalId,
  hasError,
}: InternalIdViewProps) {
  const startObject = useSelector((state: RootState) => state.startObject);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [internalId, setInternalId] = useState(startObject?.internalId || "");
  const [validationText, setValidationText] = useState("");

  useEffect(() => {
    if (!isLoading) {
      if (hasError) {
        setValidationText("Something went wrong!");
      } else if (isSubmitted && !startObject.internalId) {
        setValidationText("Employee ID not found");
      }
    }
  }, [isLoading, hasError, isSubmitted]);

  const _handleSubmitInternalId = useCallback(() => {
    onSubmitInternalId(internalId, startObject?.orgCode);
    setTimeout(() => {
      setIsSubmitted(true);
    }, 200);
  }, [internalId, startObject]);

  return (
    <View
      style={{
        marginBottom: 15,
        alignItems: "center",
      }}
    >
      <Text
        style={{
          color: skaleCommunityGray,
          fontSize: 30,
          fontWeight: "bold",
          marginBottom: 15,
          textAlign: "center",
        }}
      >
        {`Please enter your\nwork email address\nor employee ID for:\n${startObject?.name}`}
      </Text>
      <Text
        style={{
          color: textColor,
          fontSize: 16,
        }}
      >
        {`This was given to you when you first joined\n${startObject?.name}.`}
      </Text>
      <TextInput
        value={internalId}
        style={[styles.textInput, { marginTop: 20 }]}
        onChangeText={(text: string) => {
          setValidationText("");
          setInternalId(text);
          setIsSubmitted(false);
        }}
        autoCapitalize={"none"}
        autoCorrect={false}
        autoFocus={true}
      />
      {validationText && !isLoading ? (
        <View
          style={{
            marginTop: 40,
            marginBottom: 30,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              color: colorWithOpacity(skalePrimaryBlue, 0.1),
            }}
          >
            <Icon size={50} icon={exclamation} />
          </div>
          <View
            style={{
              paddingTop: screenHeight * 0.03,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Text
              style={{
                fontWeight: "bold",
                fontSize: 20,
                color: skaleBlack,
              }}
            >
              {validationText}
            </Text>
            <Text
              style={{
                fontSize: 16,
                color: skaleBlack,
                textAlign: "center",
              }}
            >
              {"Please try again."}
            </Text>
          </View>
        </View>
      ) : null}
      <Button
        marginTop={20}
        disabled={!!(validationText || isLoading || !internalId)}
        onPress={() => _handleSubmitInternalId()}
        text={isLoading ? "Submitting..." : "Submit"}
      />
    </View>
  );
}
